import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, MapPin, Bed, Bath, Maximize, Toilet, Building2, ChevronRight, Facebook, Instagram, Linkedin, Youtube, ExternalLink } from 'lucide-react'
import { motion } from 'framer-motion'
import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, { FILTERS_INITIAL_STATE, PropertyFiltersState, applyFilters } from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

const ORBE_LOGO = 'https://raw.githubusercontent.com/ruxo-rod/files/main/Orbelogotipo.png'

const MISION = 'En Orbe te acompañamos en la búsqueda de tu sueño inmobiliario, brindando una atención única que combina asesoría profesional de excelencia, trato cálido, honesto y personalizado, para superar tus expectativas, logrando de esta experiencia un momento inolvidable.'

interface Property {
  id: string
  seo_title: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  purpose: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  city?: string
  amenities?: string[]
  featured_image?: string
}

interface Seller {
  name: string
  avatar_url?: string
  subdomain: string
  specialties?: string[]
  zones?: string[]
}

interface OrbeAgencyTemplateProps {
  agent: {
    name: string
    bio: string
    phone: string
    specialties: string[]
    zones: string[]
    avatar_url?: string
    social_media?: {
      facebook?: string
      instagram?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
}

const OrbeAgencyTemplate: React.FC<OrbeAgencyTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()
  const [properties, setProperties] = useState<Property[]>([])
  const [sellers, setSellers] = useState<Seller[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)

  // CONFIG: cambiar por agent.agent_config cuando se implemente en BD
  const showPhone = false
  const showWhatsapp = false

  useEffect(() => {
    const subdomain = window.location.hostname.split('.')[0]

    const fetchProperties = async () => {
      try {
        const response = await fetch(`/api/agents/subdomain/${subdomain}/properties`)
        if (response.ok) {
          const data = await response.json()
          setProperties(data)
        }
      } catch (error) {
        console.error('Error fetching properties:', error)
      }
    }

    const fetchSellers = async () => {
      try {
        const response = await fetch(`/api/agents/subdomain/${subdomain}/sellers`)
        if (response.ok) {
          const data = await response.json()
          setSellers(data)
        }
      } catch (error) {
        console.error('Error fetching sellers:', error)
      }
    }

    fetchProperties()
    fetchSellers()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  const formatPrice = (price: number, currency: string) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`
  }

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0
    ? filteredProperties
    : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>

      {/* ===== HEADER ===== */}
      <header className="bg-[#1a1a1a] border-b border-[#C9A227]/30 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-between py-4">
            <img src={ORBE_LOGO} alt="Orbe Bienes Raíces" className="h-14 w-auto" />
            <div className="hidden md:flex items-center gap-6">
              {showPhone && agent.phone && (
                <a href={`tel:${agent.phone}`}
                  className="flex items-center gap-2 text-gray-700 hover:text-[#C9A227] transition-colors text-sm">
                  <Phone size={15} />
                  {agent.phone}
                </a>
              )}
              {agent.social_media && (
                <div className="flex gap-3">
                  {agent.social_media.facebook && (
                    <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#C9A227] transition-colors"><Facebook size={16} /></a>
                  )}
                  {agent.social_media.instagram && (
                    <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#C9A227] transition-colors"><Instagram size={16} /></a>
                  )}
                  {agent.social_media.linkedin && (
                    <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#C9A227] transition-colors"><Linkedin size={16} /></a>
                  )}
                  {agent.social_media.youtube && (
                    <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#C9A227] transition-colors"><Youtube size={16} /></a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="bg-[#1a1a1a] py-24 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src={ORBE_LOGO} alt="" className="w-[600px] h-auto opacity-[0.04]" />
        </div>
        <div className="max-w-7xl mx-auto px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="flex items-center gap-4">
                <div className="h-px w-12 bg-[#C9A227]" />
                <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase font-medium">
                  Agencia Inmobiliaria
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-light text-white leading-tight">
                Tu sueño<br />
                <span className="font-semibold">inmobiliario</span>
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed max-w-lg">
                {MISION}
              </p>
              <div className="grid grid-cols-3 gap-6 py-6 border-y border-white/10">
                {[
                  { value: properties.length, label: 'Propiedades' },
                  { value: sellers.length, label: 'Asesores' },
                  { value: agent.zones?.length || 0, label: 'Zonas' },
                ].map(({ value, label }, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl font-bold text-[#C9A227]">{value}</div>
                    <div className="text-gray-400 text-xs uppercase tracking-wider mt-1">{label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Director */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="flex flex-col items-center text-center"
            >
              <div className="relative mb-6">
                {agent.avatar_url ? (
                  <img src={agent.avatar_url} alt={agent.name}
                    className="w-48 h-48 rounded-full object-cover object-top ring-4 ring-[#C9A227]/30 shadow-2xl" />
                ) : (
                  <div className="w-48 h-48 rounded-full bg-[#C9A227]/10 border-2 border-[#C9A227]/30 flex items-center justify-center">
                    <span className="text-6xl font-light text-[#C9A227]">{agent.name.charAt(0)}</span>
                  </div>
                )}
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#C9A227] text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap tracking-wider uppercase">
                  Director
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white mt-4">{agent.name}</h2>
              <p className="text-gray-400 text-sm mt-2">Orbe Bienes Raíces</p>
              {agent.bio && (
                <p className="text-gray-400 text-sm leading-relaxed mt-4 max-w-sm">{agent.bio}</p>
              )}
              {(showPhone || showWhatsapp) && agent.phone && (
                <div className="flex gap-3 mt-6">
                  {showPhone && (
                    <a href={`tel:${agent.phone}`}
                      className="flex items-center gap-2 px-5 py-2.5 bg-[#C9A227] text-white text-sm font-semibold rounded transition-all hover:bg-[#B8943B]">
                      <Phone size={15} />
                      {agent.phone}
                    </a>
                  )}
                  {showWhatsapp && (
                    <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white text-sm font-semibold rounded transition-all hover:bg-[#1fb855]">
                      WhatsApp
                    </a>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== PROPIEDADES ===== */}
      {properties.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="h-px w-8 bg-[#C9A227]" />
                <span className="text-[#C9A227] text-xs tracking-widest uppercase font-medium">Inventario</span>
              </div>
              <h2 className="text-3xl font-light text-gray-900">
                Portafolio de <span className="font-semibold">Propiedades</span>
              </h2>
              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="gold"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(v => !v)}
              />
            </motion.div>

            {/* Tabla corporativa */}
            <div className="space-y-3 mt-10">
              <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 bg-[#1a1a1a] text-[#C9A227] text-xs font-bold uppercase tracking-wider rounded">
                <div className="col-span-4">Propiedad</div>
                <div className="col-span-2">Tipo</div>
                <div className="col-span-2">Ubicación</div>
                <div className="col-span-2">Características</div>
                <div className="col-span-2 text-right">Precio</div>
              </div>

              {paginatedProperties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title)}
                  className="group cursor-pointer bg-white border border-gray-200 hover:border-[#C9A227] rounded transition-all duration-300 hover:shadow-md overflow-hidden"
                >
                  <div className="lg:grid lg:grid-cols-12 gap-4 items-center">
                    <div className="col-span-4 flex items-center gap-4 p-4">
                      <div className="w-20 h-16 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                        {property.featured_image ? (
                          <img src={property.featured_image} alt={property.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 size={24} className="text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 group-hover:text-[#C9A227] transition-colors text-sm leading-tight truncate">
                          {property.title}
                        </h3>
                        {property.description && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{property.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 px-4 lg:px-0 pb-2 lg:pb-0">
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                        {property.property_type || 'N/A'}
                      </span>
                      <span className="block text-xs text-gray-400 mt-1 capitalize">{property.purpose}</span>
                    </div>
                    <div className="col-span-2 px-4 lg:px-0 pb-2 lg:pb-0">
                      <div className="flex items-center gap-1 text-gray-600 text-xs">
                        <MapPin size={12} className="text-[#C9A227] flex-shrink-0" />
                        <span className="truncate">{property.zone}</span>
                      </div>
                      {property.city && (
                        <span className="text-xs text-gray-400 ml-4">{property.city}</span>
                      )}
                    </div>
                    <div className="col-span-2 px-4 lg:px-0 pb-2 lg:pb-0">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {property.bedrooms && (
                          <span className="flex items-center gap-1"><Bed size={12} /> {property.bedrooms}</span>
                        )}
                        {property.bathrooms && (
                          <span className="flex items-center gap-1"><Bath size={12} /> {property.bathrooms}</span>
                        )}
                        {(property.half_bathrooms ?? 0) > 0 && (
                          <span className="flex items-center gap-1"><Toilet size={12} /> {property.half_bathrooms}</span>
                        )}
                        {property.area_m2 && (
                          <span className="flex items-center gap-1"><Maximize size={12} /> {property.area_m2}m²</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 px-4 lg:px-0 pb-4 lg:pb-0 flex items-center justify-between lg:justify-end gap-3">
                      <span className="font-bold text-gray-900 text-sm">
                        {formatPrice(property.price, property.currency)}
                      </span>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-[#C9A227] transition-colors flex-shrink-0" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <PropertyPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={setPerPage}
              colorSchemeKey="gold"
            />
          </div>
        </section>
      )}

      {/* ===== ASESORES ===== */}
      {sellers.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="h-px w-8 bg-[#C9A227]" />
                <span className="text-[#C9A227] text-xs tracking-widest uppercase font-medium">Equipo</span>
              </div>
              <h2 className="text-3xl font-light text-gray-900">
                Nuestros <span className="font-semibold">Asesores</span>
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sellers.map((seller, index) => (
                <motion.a
                  key={seller.subdomain}
                  href={`https://${seller.subdomain}.innovagent.tech`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="group bg-white border border-gray-200 hover:border-[#C9A227] rounded-lg p-6 text-center transition-all duration-300 hover:shadow-md"
                >
                  <div className="relative inline-block mb-4">
                    {seller.avatar_url ? (
                      <img src={seller.avatar_url} alt={seller.name}
                        className="w-20 h-20 rounded-full object-cover object-top mx-auto ring-2 ring-gray-100 group-hover:ring-[#C9A227]/40 transition-all" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/20 flex items-center justify-center mx-auto">
                        <span className="text-2xl font-light text-[#C9A227]">{seller.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-[#C9A227] transition-colors text-sm">
                    {seller.name}
                  </h3>
                  {seller.specialties && seller.specialties.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                      {seller.specialties.slice(0, 2).join(' · ')}
                    </p>
                  )}
                  {seller.zones && seller.zones.length > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center justify-center gap-1">
                      <MapPin size={10} className="text-[#C9A227]" />
                      {seller.zones.slice(0, 1).join(', ')}
                    </p>
                  )}
                  <div className="flex items-center justify-center gap-1 mt-3 text-[#C9A227] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Ver perfil <ExternalLink size={11} />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* ===== FOOTER ===== */}
      <footer className="bg-[#1a1a1a] border-t-2 border-[#C9A227] py-12">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={ORBE_LOGO} alt="Orbe Bienes Raíces" className="h-14 w-auto" />
            <div className="text-center md:text-right">
              <p className="text-gray-500 text-xs">
                © {new Date().getFullYear()} Orbe Bienes Raíces · Todos los derechos reservados
              </p>
              {agent.social_media && Object.values(agent.social_media).some(v => v) && (
                <div className="flex gap-4 mt-3 justify-center md:justify-end">
                  {agent.social_media.facebook && (
                    <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                      className="text-gray-500 hover:text-[#C9A227] transition-colors"><Facebook size={15} /></a>
                  )}
                  {agent.social_media.instagram && (
                    <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                      className="text-gray-500 hover:text-[#C9A227] transition-colors"><Instagram size={15} /></a>
                  )}
                  {agent.social_media.linkedin && (
                    <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                      className="text-gray-500 hover:text-[#C9A227] transition-colors"><Linkedin size={15} /></a>
                  )}
                  {agent.social_media.youtube && (
                    <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                      className="text-gray-500 hover:text-[#C9A227] transition-colors"><Youtube size={15} /></a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>

      <ChatWidget
        agentName={agent.name}
        agentSubdomain={window.location.hostname.split('.')[0]}
        agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
      />
    </div>
  )
}

export default OrbeAgencyTemplate
